import { loadPortalSession } from "./portalSession";

/**
 * Print who this tab is signed in as: email, partner code, branch code.
 *
 * Both b2b2c consoles keep their identity in sessionStorage and nowhere on
 * screen — the header shows a name, and everything that actually scopes a
 * request (which partner, which branch) is invisible. So "is this session
 * pointed at the right tenant?" could only be answered by expanding
 * `_zenvoya_partner_session` in devtools. This answers it on sign-in instead,
 * for both portals, from the SAVED session rather than the login response, so
 * what it prints is what the rest of the console actually reads after a reload.
 *
 * ── The branch code is RESOLVED, not read ───────────────────────────────────
 * `user.branchCode` is NULL for essentially every partner user, and permanently:
 * app-backend creates a tenant's first user with `branch_code: undefined` before
 * the branches exist, and only the Users screen ever sets one. NULL there is the
 * "spans the whole partner" sentinel, not missing data — the session still acts
 * at the partner's DEFAULT branch, which is what app-backend's
 * `resolveTenantBranch` asserts outbound to the engines.
 *
 * Printing the raw field would therefore show `branchCode: null` on a session
 * that is scoping every request to a real branch — a log that reads as a bug and
 * sends whoever is looking at it after the wrong thing. So the same fallback the
 * server uses is applied here, and `branchSource` says which rung answered.
 */

/** What each stored session key is called on screen. */
const PORTAL_LABEL = {
  partner: "partnerAdmin",
  crm: "admin",
};

/**
 * The partner's default branch, picked the way the server picks it.
 *
 * `branches` arrives ordered `is_default DESC, createdAt ASC, branch_code ASC`
 * (PartnerAuthRepository.contextFor), which is the same ordering
 * `defaultBranchFor` uses to stamp rows — so taking the flagged one, and the
 * first otherwise, lands on the branch app-backend would have chosen.
 */
const defaultBranchOf = (branches) => {
  const list = Array.isArray(branches) ? branches : [];
  return list.find((b) => b?.isDefault)?.id ?? list[0]?.id ?? null;
};

/**
 * The identity fields for a stored portal session, or null when signed out.
 *
 * @param {"partner"|"crm"} portal which session to read
 */
export const sessionIdentity = (portal) => {
  const session = loadPortalSession(portal);
  if (!session) return null;

  // A branch the session actually asserted wins over the fallback: the partner
  // console can switch branch mid-session, and that choice — not the user row —
  // is what its token then carries.
  const asserted = session.actingBranchCode ?? session.user?.branchCode ?? null;
  const branchCode = asserted ?? defaultBranchOf(session.branches);

  return {
    email: session.user?.email ?? null,
    partnerCode: session.partner?.partnerCode ?? null,
    branchCode,
    branchSource:
      session.actingBranchCode != null
        ? "switched branch"
        : session.user?.branchCode != null
          ? "user row"
          : branchCode != null
            ? "partner default (user spans all branches)"
            : "none — this partner has no branch",
  };
};

/**
 * Log that identity.
 *
 * Deliberately a plain `console.log` and not the environment-gated `consoleLog`
 * from `helpers/index`: the sessions worth checking are the ones on the deployed
 * hosts, and a probe that goes quiet exactly where it is needed is no probe.
 *
 * @param {"partner"|"crm"} portal
 * @param {string} label  what prompted the log (e.g. "login", "branch switch")
 * @param {object} [extra] extra fields to print beside the identity
 */
export const logSessionIdentity = (portal, label = "", extra = null) => {
  if (typeof window === "undefined") return;

  const name = PORTAL_LABEL[portal] || portal;
  const prefix = `[session] ${name}${label ? ` — ${label}` : ""}`;
  const identity = sessionIdentity(portal);

  if (!identity) {
    console.log(`${prefix}: not signed in`);
    return;
  }
  console.log(prefix, extra ? { ...identity, ...extra } : identity);
};
